import { Brain, Bot, Coins } from "lucide-react";
import { Corners, GlowButton, PageHead, Reveal, SectionHeader } from "@/components/ui-kit";

const PILLARS = [
  { icon: Brain, title: "INTELLIGENCE", desc: "AI agents reason and coordinate. They decompose objectives, discover machines and negotiate the terms of work." },
  { icon: Bot, title: "MACHINES", desc: "Robots execute physical tasks. Rovers, drones, humanoids and industrial systems turn coordination into action." },
  { icon: Coins, title: "ECONOMY", desc: "Machines interact with resources and value. Payments, reputation and settlement make participation sustainable." },
];

const AboutPage = () => (
  <div data-testid="about-page" className="pb-24">
    <PageHead
      eyebrow="MODULE 09 // MISSION"
      title="WHY ARCMECH?"
      subtitle="ARCMECH explores what happens when artificial intelligence, robotics and programmable economic infrastructure converge."
    />
    <div className="px-5 sm:px-8 max-w-7xl mx-auto space-y-20">
      <section className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <SectionHeader index="TRIAD // CONVERGENCE" title={"THREE FORCES.\nONE ECONOMY."} subtitle="None of the three is sufficient alone. Intelligence without machines stays digital. Machines without intelligence stay scripted. Both without an economy stay dependent." />
          <GlowButton to="/thesis" variant="ghost" testId="about-thesis-link" cursor="OPEN">READ THE THESIS</GlowButton>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="hud-panel relative p-4 sm:p-6">
            <Corners />
            <svg viewBox="0 0 400 340" className="w-full h-auto" role="img" aria-label="Triangle connecting intelligence, machines and economy with ARCMECH at the center">
              <line x1="200" y1="50" x2="60" y2="280" stroke="rgba(0,168,255,0.4)" strokeWidth="1" className="anim-dash" />
              <line x1="200" y1="50" x2="340" y2="280" stroke="rgba(0,168,255,0.4)" strokeWidth="1" className="anim-dash" />
              <line x1="60" y1="280" x2="340" y2="280" stroke="rgba(0,168,255,0.4)" strokeWidth="1" className="anim-dash" />
              <line x1="200" y1="50" x2="200" y2="195" stroke="rgba(67,217,255,0.3)" strokeWidth="1" strokeDasharray="2 6" />
              <line x1="60" y1="280" x2="200" y2="195" stroke="rgba(67,217,255,0.3)" strokeWidth="1" strokeDasharray="2 6" />
              <line x1="340" y1="280" x2="200" y2="195" stroke="rgba(67,217,255,0.3)" strokeWidth="1" strokeDasharray="2 6" />
              {[[200, 50, "INTELLIGENCE"], [60, 280, "MACHINES"], [340, 280, "ECONOMY"]].map(([x, y, label]) => (
                <g key={label}>
                  <circle cx={x} cy={y} r="26" fill="#06101C" stroke="#00A8FF" strokeOpacity="0.6" />
                  <circle cx={x} cy={y} r="5" fill="#43D9FF" />
                  <text x={x} y={y + (y < 100 ? -36 : 44)} textAnchor="middle" fill="#C9D2DC" fontSize="11" fontFamily="JetBrains Mono, monospace" letterSpacing="2">{label}</text>
                </g>
              ))}
              <circle cx="200" cy="195" r="44" fill="#020406" stroke="#1688FF" strokeWidth="1.5" />
              <text x="200" y="200" textAnchor="middle" fill="#F4F8FC" fontSize="13" fontWeight="700" fontFamily="Syne, sans-serif" letterSpacing="2">ARCMECH</text>
            </svg>
          </div>
        </Reveal>
      </section>
      <section className="grid md:grid-cols-3 gap-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <div data-testid={`pillar-${p.title.toLowerCase()}`} className="hud-panel relative p-6 h-full group hover:border-arc-electric/50 transition-colors duration-300">
              <Corners className="border-arc-electric/0 group-hover:border-arc-electric/70 transition-colors duration-300" />
              <p.icon size={24} className="text-arc-electric mb-4" aria-hidden="true" />
              <h3 className="font-display text-lg font-bold tracking-wider text-arc-white">{p.title}</h3>
              <p className="mt-3 text-sm text-arc-silver/60 leading-relaxed">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </section>
      <Reveal>
        <blockquote className="max-w-3xl mx-auto text-center border-y border-arc-blue/20 py-10">
          <p className="font-display text-xl sm:text-2xl text-arc-white leading-relaxed">"ARCMECH explores what happens when artificial intelligence, robotics and programmable economic infrastructure converge."</p>
          <footer className="mt-4 font-mono text-[10px] tracking-[0.3em] text-arc-electric">CORE STATEMENT // ARCMECH</footer>
        </blockquote>
      </Reveal>
    </div>
  </div>
);

export default AboutPage;
